import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { BookOpen, Star, Users, Trophy, Play, ChevronRight } from "lucide-react";

export default function Landing() {
  const navigate = useNavigate();

  const features = [
    {
      icon: <BookOpen className="w-6 h-6" />,
      title: "Beautiful Quran Display",
      description: "Crystal-clear Uthmani script with customizable fonts and sizes for comfortable reading"
    },
    {
      icon: <Play className="w-6 h-6" />,
      title: "Audio Looping",
      description: "Loop verses or ranges with adjustable repeat counts and pause durations between ayahs"
    },
    {
      icon: <Trophy className="w-6 h-6" />,
      title: "Track Progress",
      description: "Visual progress tracking for each surah. Mark verses as memorized and see your journey"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Family Friendly",
      description: "Multiple profiles for the whole family. Everyone can track their own memorization"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1544685472-cca59893ac0c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxvcGVuJTIwcXVyYW4lMjBib29rJTIwcmVoYWwlMjBkYXJrJTIwYWVzdGhldGljfGVufDB8fHx8MTc2ODUwNjI5NXww&ixlib=rb-4.1.0&q=85')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-islamic-blue-950/90 via-islamic-blue-950/80 to-islamic-blue-950"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-6xl mx-auto px-6 py-20 text-center">
          {/* Arabic Calligraphy */}
          <p className="font-amiri text-gold-400 text-2xl mb-4 animate-fade-in">
            بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ
          </p>

          {/* Main Heading */}
          <h1 className="font-cinzel text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 animate-fade-in stagger-1">
            <span className="gold-text">Memorize the Quran</span>
            <br />
            <span className="text-slate-200">with Ease & Focus</span>
          </h1>

          {/* Subheading */}
          <p className="font-outfit text-slate-400 text-base sm:text-lg max-w-2xl mx-auto mb-10 animate-fade-in stagger-2">
            A modern memorization companion designed for spiritual growth. 
            Beautiful interface, powerful audio controls, and progress tracking 
            to guide your Hifz journey.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in stagger-3">
            <Button 
              data-testid="get-started-btn"
              onClick={() => navigate('/register')}
              className="btn-gold rounded-full px-8 py-6 text-lg font-outfit font-medium"
            >
              Start Memorizing
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              data-testid="login-btn"
              variant="outline"
              onClick={() => navigate('/login')}
              className="btn-outline-gold rounded-full px-8 py-6 text-lg font-outfit"
            >
              Sign In
            </Button>
          </div>

          {/* Stats */}
          <div className="mt-16 grid grid-cols-3 gap-8 max-w-lg mx-auto animate-fade-in stagger-4">
            <div>
              <p className="font-cinzel text-3xl font-bold text-gold-400">114</p>
              <p className="text-slate-500 text-sm font-outfit">Surahs</p>
            </div>
            <div>
              <p className="font-cinzel text-3xl font-bold text-gold-400">6,236</p>
              <p className="text-slate-500 text-sm font-outfit">Ayahs</p>
            </div>
            <div>
              <p className="font-cinzel text-3xl font-bold text-gold-400">30</p>
              <p className="text-slate-500 text-sm font-outfit">Juz</p>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-gold-400/30 rounded-full flex justify-center pt-2">
            <div className="w-1 h-2 bg-gold-400 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-cinzel text-3xl sm:text-4xl font-bold text-slate-100 mb-4">
              Everything You Need for <span className="text-gold-400">Hifz</span>
            </h2>
            <p className="text-slate-400 font-outfit max-w-xl mx-auto">
              Combining traditional memorization techniques with modern technology
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {features.map((feature) => (
              <div 
                key={feature.title}
                className="glass-card rounded-xl p-8 card-lift"
              >
                <div className="w-12 h-12 rounded-lg bg-gold-400/10 flex items-center justify-center text-gold-400 mb-4">
                  {feature.icon}
                </div>
                <h3 className="font-cinzel text-xl font-semibold text-slate-100 mb-2">
                  {feature.title}
                </h3>
                <p className="text-slate-400 font-outfit">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Preview Section */}
      <section className="py-24 px-6 bg-islamic-blue-900/30">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-cinzel text-3xl sm:text-4xl font-bold text-slate-100 mb-6">
                Immersive <span className="text-gold-400">Reading</span> Experience
              </h2>
              <p className="text-slate-400 font-outfit mb-8">
                Our reader is designed to minimize distractions and maximize focus. 
                Large, clear Arabic text with optional translations, audio controls 
                right at your fingertips, and progress tracking that motivates.
              </p>
              <ul className="space-y-4">
                {[
                  "High-quality Uthmani script",
                  "Multiple reciters to choose from",
                  "Verse-by-verse audio looping",
                  "Customizable pause between verses"
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-slate-300 font-outfit">
                    <Star className="w-4 h-4 text-gold-400 fill-gold-400" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="glass-card rounded-2xl p-8 gold-glow">
              {/* Mock Reader Preview */}
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <span className="text-gold-400 font-cinzel">Al-Fatiha</span>
                  <span className="text-slate-500 text-sm font-outfit">1:1</span>
                </div>
                <p className="font-noto-naskh text-3xl text-slate-100 text-right leading-[2.5] arabic-text">
                  بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ
                </p>
                <p className="text-slate-400 font-outfit text-sm">
                  In the name of Allah, the Entirely Merciful, the Especially Merciful.
                </p>
                <div className="flex items-center gap-4 pt-4 border-t border-white/5">
                  <div className="flex-1 h-1 bg-islamic-blue-800 rounded-full overflow-hidden">
                    <div className="h-full w-1/7 bg-gold-400 rounded-full"></div>
                  </div>
                  <span className="text-slate-500 text-xs font-outfit">1 of 7</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <p className="font-amiri text-gold-400 text-xl mb-4">
            وَلَقَدْ يَسَّرْنَا الْقُرْآنَ لِلذِّكْرِ فَهَلْ مِن مُّدَّكِرٍ
          </p>
          <p className="text-slate-400 font-outfit mb-8 italic">
            &ldquo;And We have certainly made the Quran easy to remember. So is there anyone who will be mindful?&rdquo; - 54:17
          </p>
          <Button 
            data-testid="cta-get-started-btn"
            onClick={() => navigate('/register')}
            className="btn-gold rounded-full px-10 py-6 text-lg font-outfit font-medium gold-glow-hover"
          >
            Begin Your Journey
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm font-outfit">
            Quran Memorization App
          </p>
          <p className="text-slate-600 text-xs font-outfit">
            Built with reverence for the sacred text
          </p>
        </div>
      </footer>
    </div>
  );
}
